import React, { Component } from 'react'
import { Trans, withTranslation } from 'react-i18next'
import './styles.scss'
import ImageBrowserItem from './Item'
import Lightbox from '../../../Shared/Lightbox'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'
import { reOrderImages } from '../../../../api'
import Alert from '../../../Shared/Alert'
import UploadZone from '../../../Shared/UploadZone'
import UploadItem from './uploadItem'
import PropTypes from 'prop-types'
import Loader from '../../../Shared/Loader'
import { arrayMoveImmutable } from 'array-move'
import { isImageConfirmed } from './utils'
import activityTrackingService from '../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../constants'
import LayerConfirmImages from './Layer/LayerConfirmImages'
import PageHead from '../../../Shared/PageHead'
import Button from '../../../Shared/Form/Button'
import PublishAllButton from '../PublishAllButton'

const SortableImageBrowserItem = SortableElement(({
                                                    sortIndex,
                                                    image,
                                                    rooms,
                                                    roomAttributes,
                                                    onHeroClick,
                                                    onZoomClick,
                                                    onImageChanged,
                                                    isGiataUser,
                                                    permissions,
                                                    isSelected,
                                                    onImageSelectChange,
                                                    selectedImagesCount
                                                  }) => (
  <ImageBrowserItem key={image.id + '_ibi'} number={sortIndex} rooms={rooms}
                    roomAttributes={roomAttributes} image={image}
                    onHeroClick={onHeroClick} onZoomClick={onZoomClick}
                    onImageChanged={onImageChanged} isGiataUser={isGiataUser}
                    permissions={permissions} isSelected={isSelected}
                    onImageSelectChange={onImageSelectChange}
                    selectedImagesCount={selectedImagesCount}/>
))

const SortableImageBrowserItems = SortableContainer(({
                                                       images,
                                                       uploadQueue,
                                                       rooms,
                                                       roomAttributes,
                                                       onHeroClick,
                                                       onZoomClick,
                                                       onUploadCancel,
                                                       onUploadQueueImageRemove,
                                                       onImageChanged,
                                                       openDropzoneDialog,
                                                       isGiataUser,
                                                       permissions,
                                                       selectedImageIdsMap,
                                                       onImageSelectChange
                                                     }) => {
  const selectedImagesCount = Object.keys(selectedImageIdsMap).length
  return (
    <div className="Imagebrowser-Items clearfix">
      <div className="Imagebrowseritem Imagebrowseritem-Add Gradient" onClick={() => openDropzoneDialog()}>
        <div>
          <p className="UploadZone-Add">
            <Trans>LABEL_ADD_IMAGE</Trans></p>
        </div>
        <span><Trans>HINT_TEXT_ADD_IMAGE_WITH_DRAG</Trans></span>
      </div>
      {images.map((image, index) => {
        return (
          <SortableImageBrowserItem key={image.id + '_SortableItem'} index={index} sortIndex={index} image={image}
                                    rooms={rooms} roomAttributes={roomAttributes} onHeroClick={onHeroClick}
                                    onZoomClick={onZoomClick} onImageChanged={onImageChanged}
                                    isGiataUser={isGiataUser}
                                    permissions={permissions}
                                    isSelected={selectedImageIdsMap.hasOwnProperty(image.id)}
                                    onImageSelectChange={onImageSelectChange}
                                    selectedImagesCount={selectedImagesCount}/>
        )
      })}
      {uploadQueue.map((image, index) => {
        return (
          <UploadItem image={image} key={index + '_imageToUpload'} onUploadCancel={onUploadCancel}
                      onRemove={onUploadQueueImageRemove}/>
        )
      })}
    </div>
  )
})

const isGiataMailRegEx = /^.*@(giata\.de|giata\.com)$/i

class ImageBrowser extends Component {

  constructor (props) {
    super(props)
    this.state = {
      activeImage: 0,
      error: null,
      loading: false,
      layerConfirmSave: false
    }
    this.dropzoneRef = React.createRef()
    this.lightboxRef = React.createRef()
  }

  componentDidMount () {
    activityTrackingService.trackPageView('Upload Images', {
      giataId: this.props.property.giataId,
    })
  }

  onUploadDrop = (files) => {
    activityTrackingService.trackEvent(trackingEvent.uploadImages, {
      giataId: this.props.property.giataId,
      numberOfImages: files.length,
      page: 'Upload Images'
    })
    this.props.onUploadFiles(files)
  }

  onSortEnd = ({ oldIndex, newIndex }) => {
    this.saveImageSequence(oldIndex, newIndex)
  }

  onHeroClick = (imageId, isHero) => {
    const newImages = []
    this.props.images.forEach((image) => {
      image.hero = isHero && image.id === imageId
      newImages.push(image)
    })
    this.props.onImagesChange(newImages)
  }

  onImageChanged = (newImage) => {
    let index = this.props.images.findIndex(i => i.id === newImage.id)
    if (index > -1) {
      const newImages = [...this.props.images]
      newImages[index] = newImage
      this.props.onImagesChange(newImages)
    }
  }

  saveImageSequence = (oldIndex, newIndex) => {
    if (oldIndex === newIndex) {
      return
    }
    activityTrackingService.trackEvent(trackingEvent.reorderImage)
    const newImages = arrayMoveImmutable(this.props.images, oldIndex, newIndex)
    const imageIdsOrdered = newImages.map(function (image) {
      return image.id
    })
    const { fetchPromise } = reOrderImages(this.props.property.giataId, imageIdsOrdered)
    // change early for better ux, revert on error the order
    this.props.onImagesChange(newImages)

    this.setState({ loading: true }, () => {
      fetchPromise.then(() => {
        this.setState({ loading: false })
      }).catch(() => {
        const newImages = arrayMoveImmutable(this.props.images, newIndex, oldIndex) // revert change
        this.props.onImagesChange(newImages)
        this.setState({ loading: false })
      })
    })
  }

  onSaveClick = () => {
    activityTrackingService.trackEvent(trackingEvent.triggerImageConfirmationPrompt)
    this.setState({ layerConfirmSave: true })
  }

  onUploadQueueImageRemove = (image) => {
    const index = this.props.uploadQueue.indexOf(image)
    if (index > -1) {
      let uploadQueueNew = [...this.props.uploadQueue]
      uploadQueueNew.splice(index, 1)
      this.props.onUploadQueueChange(uploadQueueNew)
    }
  }

  onImageSelectChange = (imageId, selected) => {
    const newSelectedImageIdsMap = Object.assign({}, this.props.selectedImageIdsMap)
    if (!selected) {
      delete newSelectedImageIdsMap[imageId]
    } else {
      newSelectedImageIdsMap[imageId] = true
    }
    this.props.onSelectedImageIdsMapChange(newSelectedImageIdsMap)
  }

  openDropzoneDialog = () => {
    if (this.dropzoneRef.current) {
      this.dropzoneRef.current.open()
    }
  }
  onImagesConfirmed = () => {
    const newImages = []
    this.props.images.forEach((image) => {
      image.confirmed = true
      newImages.push(image)
    })
    this.props.onImagesChange(newImages)
    window.scrollTo({ top: 0 })
  }

  onZoomClick = (index) => {
    this.setState({
      activeImage: index
    })

    setTimeout(() => {
      if (this.lightboxRef.current) {
        this.lightboxRef.current.openLightbox()
      }
    }, 300)
  }

  render () {
    const isGiataUser = isGiataMailRegEx.test(this.props.user.user)
    const unconfirmedImages = this.props.images.filter(image => !isImageConfirmed(image)).length
    const { t } = this.props
    const saveDisabled = !this.props.showSaveButton || unconfirmedImages === 0
    return (
      <div className="page" id={'image-browser-page'}>
        {this.state.loading && <Loader/>}
        {this.state.error && <Alert type={'danger'}>{this.state.error}</Alert>}
        {!this.state.error &&
          <React.Fragment>
            <PageHead
              title={t('IMAGE_GALLERY')}
              subtitle={t('IMAGE_GALLERY_SUBHEADER')}
            >
              <Button
                notificationBadge={unconfirmedImages > 0 ? unconfirmedImages : undefined}
                disabled={saveDisabled}
                onClick={this.onSaveClick}
                classNames={saveDisabled ? 'Button-Primary-v2 Button-Min-Width Button-Icon-Save-Grey' : 'Button-Primary-v2 Button-Min-Width Button-Icon-Save'}
              >
                {this.props.t('CONFIRM_IMAGES')}
              </Button>
              <PublishAllButton giataId={this.props.property.giataId}/>
            </PageHead>
            <Lightbox images={this.props.images} index={this.state.activeImage} ref={this.lightboxRef}/>
            <UploadZone onDrop={this.onUploadDrop} dropzoneRef={this.dropzoneRef}>
              <div className="ImageBrowser clearfix" id="ImageBrowser">
                <div style={{ padding: 0 }} className="container clearfix">
                  <SortableImageBrowserItems axis="xy" useDragHandle={true}
                                             helperContainer={document.getElementById('ImageBrowser')}
                                             onSortEnd={this.onSortEnd} images={this.props.images}
                                             uploadQueue={this.props.uploadQueue}
                                             rooms={this.props.rooms}
                                             roomAttributes={this.props.roomAttributes}
                                             onHeroClick={this.onHeroClick}
                                             onZoomClick={this.onZoomClick}
                                             onUploadCancel={this.onUploadCancel}
                                             onImageChanged={this.onImageChanged}
                                             onUploadQueueImageRemove={this.onUploadQueueImageRemove}
                                             openDropzoneDialog={this.openDropzoneDialog}
                                             useWindowAsScrollContainer={true}
                                             permissions={this.props.permissions}
                                             isGiataUser={isGiataUser}
                                             selectedImageIdsMap={this.props.selectedImageIdsMap}
                                             onImageSelectChange={this.onImageSelectChange}
                  />
                </div>
              </div>
            </UploadZone>
            {this.props.showSaveButton &&
              <React.Fragment>
                <LayerConfirmImages
                  images={this.props.images} onConfirmed={this.onImagesConfirmed}
                  user={this.props.user.user} show={this.state.layerConfirmSave}
                  onHideClick={() => this.setState({ layerConfirmSave: false })}
                />
              </React.Fragment>
            }
          </React.Fragment>

        }
      </div>
    )
  }
}

ImageBrowser.propTypes = {
  property: PropTypes.object.isRequired,
  images: PropTypes.array.isRequired,
  rooms: PropTypes.array.isRequired,
  roomAttributes: PropTypes.object.isRequired,
  showSaveButton: PropTypes.bool.isRequired,
  permissions: PropTypes.object.isRequired,
  uploadQueue: PropTypes.array.isRequired,
  onImagesChange: PropTypes.func.isRequired,
  onUploadQueueChange: PropTypes.func.isRequired,
  onUploadFiles: PropTypes.func.isRequired,
  selectedImageIdsMap: PropTypes.object.isRequired,
  onSelectedImageIdsMapChange: PropTypes.func.isRequired
}
export default withTranslation()(ImageBrowser)
